import { AxiosResponse } from 'axios';
import { LogInResponseDTO } from '../DTO/authDTO';
import { axiosClient } from '../network/appClient';
import { useToken } from '../store/auth/tokenStore';
import { isStaleSessionFailure, sessionTokenFromAuthorization } from '../helpers/passageToken';
import { QueryClient } from '@tanstack/react-query';
import { InputCheckoutProps } from '../@types/ServiceCheckoutInput';

interface ErrorResponse {
    status: number;
    parameter: string;
}

export const handleResponse = <T>(
    rawResponse: AxiosResponse<ErrorResponse | T>
): [T, null] | [null, string] => {
    const body = rawResponse.data!;

    if (rawResponse.status === 401) {
        // Só apaga a sessão se o pedido que falhou usou a sessão atual: um 401
        // atrasado de um pedido feito com a sessão antiga não pode apagar a
        // sessão nova que acabou de chegar do portal (token de passagem).
        const failedToken = sessionTokenFromAuthorization(rawResponse.config?.headers?.Authorization);
        if (!isStaleSessionFailure(failedToken, useToken.getState().token)) {
            useToken.getState().setToken(null);
        }
        return [null, 'Internal.SessionExpired'];
    }

    if (typeof body === 'object' && 'status' in body) {
        const errorResponse = body as ErrorResponse;
        return [null, errorResponse.parameter];
    }

    return [body, null];
};

const sendLoginRequest = async (payload: {
    login: string;
    password: string;
}): Promise<{
    user?: LogInResponseDTO;
    token?: string;
    error?: string;
}> => {
    const payloadFormBase = `${payload.login}:${payload.password}`;

    const rawResponse = await axiosClient.post(
        'Login/SignIn',
        {},
        {
            headers: {
                Authorization: `Basic ${window.btoa(payloadFormBase)}`,
                'Content-Type': 'application/json',
            },
            validateStatus: () => true,
        }
    );

    const [body, error] = handleResponse<LogInResponseDTO>(rawResponse);
    if (error) return { error };

    return { user: body!, token: rawResponse.headers['x-access-token'] };
};

const sendRecoverPasswordRequest = async (payload: {
    login: string;
}): Promise<{ success?: boolean; error?: string }> => {
    const rawResponse = await axiosClient.patch(
        'Login/RecoveryPassword',
        payload,
        {
            headers: {
                Authorization: `Bearer ${useToken.getState().token}`,
            },
            validateStatus: () => true,
        }
    );

    const [, error] = handleResponse<LogInResponseDTO>(rawResponse);

    if (error) return { error };

    return { success: true };
};

const fetchMyProfile = async (): Promise<{
    profile?: LogInResponseDTO;
    error?: string;
}> => {
    const rawResponse = await axiosClient.get('MyProfile', {
        headers: {
            Authorization: `Bearer ${useToken.getState().token}`,
        },
        validateStatus: () => true,
    });

    const [profile, error] = handleResponse<LogInResponseDTO>(rawResponse);
    if (error || !profile) return { error };

    return { profile: profile };
};

const logMeOut = async (queryClient: QueryClient) => {
    useToken.getState().setToken(null);
    queryClient.resetQueries({ queryKey: ['MyProfile'] });

    setTimeout(() => {
        window.location.reload();
    }, 1000)
};

const sendSignUpRequest = async (payload: {
    name: string;
    phone: string;
    document: string;
    birthDate: Date;
    email: string;
    foreigner: boolean;
    password: string;
    passwordConfirmation: string;
    address: InputCheckoutProps["address"]
}): Promise<{
    body?: never;
    error?: string;
}> => {
    const rawResponse = await axiosClient.post(
        '/OpenSea',
        {
            ...payload,
        },
        {
            headers: {},
            validateStatus: () => true,
        }
    );

    const [, error] = handleResponse<LogInResponseDTO>(rawResponse);

    if (error) return { error };

    return {};
};

const authenticationService = {
    sendSignUpRequest,
    sendLoginRequest,
    fetchMyProfile,
    sendRecoverPasswordRequest,
    logMeOut,
};

export default authenticationService;
